import { Block, Floor, ApartmentUnit, Parking, Amenity } from '@/types/resources/property';
import dayjs from 'dayjs';

// Premium Apartment Society: Sunset Gardens
const WORKSPACE_ID = 'demo-workspace';

// Helper function to create workspace reference
const createWorkspaceRef = () => ({
  id: WORKSPACE_ID,
  name: 'Jardins do Pôr do Sol',
  slug: 'demo-workspace'
});

// Helper function to create activity records
const createActivityRecord = (type: 'created' | 'updated' | 'deleted') => ({
  timestamp: dayjs().subtract(Math.floor(Math.random() * 30), 'days').toDate(),
  type,
  performedBy: {
    id: 'admin-001',
    displayName: 'Gerente de Propriedade'
  }
});

// Create 10 blocks with uniform, modern naming and proper floor structure
export const blocks: Block[] = [
  {
    id: 'BLK-A',
    name: 'Torre A',
    description: 'Localizada no canto nordeste dos Jardins do Pôr do Sol, com vista para a entrada principal e horizonte da cidade',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-a-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre A`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-B',
    name: 'Torre B',
    description: 'Situada na seção noroeste, adjacente ao jardim central e área de jogos infantis',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-b-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre B`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-C',
    name: 'Torre C',
    description: 'Torre central posicionada perto do clube principal e complexo de piscina',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-c-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre C`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-D',
    name: 'Torre D',
    description: 'Localizada no canto sudeste, de frente para as quadras de tênis e instalações esportivas ao ar livre',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-d-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre D`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-E',
    name: 'Torre E',
    description: 'Posicionada na área sudoeste, perto do jardim e pista de caminhada',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-e-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre E`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-F',
    name: 'Torre F',
    description: 'Torre central perto da fonte principal e salão de festas, oferecendo vistas premium do complexo',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-f-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Andar`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} andar da Torre F`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-G',
    name: 'Torre G',
    description: 'Located in the east wing, adjacent to the library and reading room facilities',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-g-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower G`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-H',
    name: 'Tower H',
    description: 'Situated in the west wing near the spa and wellness center',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-h-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower H`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-I',
    name: 'Tower I',
    description: 'Positioned in the north-central area, close to the gym and fitness center',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-i-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower I`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-J',
    name: 'Tower J',
    description: 'Located in the south-central area, near the BBQ area and indoor games room',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-j-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Floor`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} floor of Tower J`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Helper function for ordinal suffixes
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

// Create floors array for compatibility (flattened from all blocks)
export const floors: Floor[] = blocks.flatMap(block => block.floors || []);

// Create apartment units for each floor with proper numbering
export const units: ApartmentUnit[] = blocks.flatMap(block => {
  const blockUnits: ApartmentUnit[] = [];
  
  // Get floors for this block
  const blockFloors = block.floors || [];
  
  blockFloors.forEach(floor => {
    // Extract floor number from floor name (e.g., "1st Floor" -> 1)
    const floorNumber = parseInt(floor.name.split(' ')[0]);
    
    // Determine units per floor based on block (some variation for realism)
    let unitsPerFloor = 4; // Default
    if (block.id === 'BLK-A' || block.id === 'BLK-C' || block.id === 'BLK-F' || block.id === 'BLK-I') {
      unitsPerFloor = 6; // 20-floor towers get more units
    } else if (block.id === 'BLK-B' || block.id === 'BLK-E' || block.id === 'BLK-G') {
      unitsPerFloor = 5; // 10-floor towers get medium units
    } else {
      unitsPerFloor = 4; // 8-floor towers get fewer units
    }
    
    for (let unitNum = 1; unitNum <= unitsPerFloor; unitNum++) {
      const unitTypes = ['1BHK', '2BHK', '3BHK', '4BHK'];
      const unitType = unitTypes[Math.floor(Math.random() * unitTypes.length)];
      const area = unitType === '1BHK' ? 650 : unitType === '2BHK' ? 950 : unitType === '3BHK' ? 1250 : 1600;
      
      // Create proper unit number (e.g., 101, 102, 201, 202)
      const unitNumber = `${floorNumber}${unitNum.toString().padStart(2, '0')}`;
      
      blockUnits.push({
        id: `${block.id}-${unitNumber}`,
        number: unitNumber,
        bedrooms: parseInt(unitType[0]),
        bathrooms: Math.floor(Math.random() * 2) + 1,
        area: area,
        status: Math.random() > 0.2 ? 'occupied' : 'vacant',
        block: {
          id: block.id,
          name: block.name
        },
        floor: {
          name: floor.name,
          slug: floor.slug
        },
        description: `${unitType} apartment on ${floor.name} of ${block.name}`,
        workspace: createWorkspaceRef(),
        activityRecords: [createActivityRecord('created')]
      });
    }
  });
  
  return blockUnits;
});

// Create parking spots for all units
export const parkings: Parking[] = units.map((unit, index) => {
  const parkingTypes = ['car', 'bike', 'other'];
  const parkingType = parkingTypes[Math.floor(Math.random() * parkingTypes.length)] as 'car' | 'bike' | 'other';
  
  return {
    id: `PARK-${(index + 1).toString().padStart(3, '0')}`,
    number: `P${(index + 1).toString().padStart(3, '0')}`,
    type: parkingType,
    status: unit.status === 'occupied' ? 'occupied' : 'available',
    apartmentUnit: unit.status === 'occupied' ? {
      id: unit.id,
      number: unit.number
    } : undefined,
    block: unit.block,
    pricing: {
      amount: parkingType === 'car' ? 100 : parkingType === 'bike' ? 50 : 75,
      currency: 'USD',
      period: 'monthly'
    },
    description: `${parkingType} parking spot for ${unit.block?.name}`,
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  };
});

// Create comprehensive amenities for a premium apartment society
export const amenities: Amenity[] = [
  {
    id: 'AMEN-001',
    name: 'Piscina',
    type: 'swimming_pool',
    description: 'Piscina olímpica com controle de temperatura',
    capacity: 50,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-C',
      name: 'Torre C'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-002',
    name: 'Academia e Centro de Fitness',
    type: 'gym',
    description: 'Academia totalmente equipada com equipamentos modernos e treinadores pessoais',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '05:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Torre A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-003',
    name: 'Casa do Clube',
    type: 'community_hall',
    description: 'Casa do clube multiuso para eventos e reuniões',
    capacity: 200,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-B',
      name: 'Torre B'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-004',
    name: 'Quadra de Tênis',
    type: 'other',
    description: 'Quadra de tênis profissional com refletores',
    capacity: 4,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-D',
      name: 'Torre D'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-005',
    name: 'Área de Jogos Infantis',
    type: 'playground',
    description: 'Área de jogos segura e divertida para crianças com equipamentos modernos',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '20:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Torre E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-006',
    name: 'Salão de Festas',
    type: 'community_hall',
    description: 'Elegante salão de festas para celebrações e eventos',
    capacity: 150,
    status: 'active',
    operatingHours: {
      open: '10:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-F',
      name: 'Torre F'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-007',
    name: 'Biblioteca e Sala de Leitura',
    type: 'other',
    description: 'Biblioteca tranquila com extensa coleção de livros e áreas de estudo',
    capacity: 25,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-G',
      name: 'Torre G'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-008',
    name: 'Spa e Centro de Bem-estar',
    type: 'other',
    description: 'Spa relaxante com terapia de massagem e tratamentos de bem-estar',
    capacity: 10,
    status: 'active',
    operatingHours: {
      open: '09:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-H',
      name: 'Torre H'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-009',
    name: 'Área de Churrasco',
    type: 'other',
    description: 'Área de churrasco ao ar livre com assentos e instalações de cozinha',
    capacity: 15,
    status: 'active',
    operatingHours: {
      open: '16:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-I',
      name: 'Torre I'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-010',
    name: 'Sala de Jogos Interior',
    type: 'other',
    description: 'Sala de jogos com tênis de mesa, carrom e jogos de tabuleiro',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-J',
      name: 'Torre J'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-011',
    name: 'Sala de Yoga e Meditação',
    type: 'other',
    description: 'Espaço tranquilo para yoga, meditação e aulas de bem-estar',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Torre A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-012',
    name: 'Jardim e Pista de Caminhada',
    type: 'garden',
    description: 'Jardim bonito com pista de caminhada e áreas de assento',
    capacity: 100,
    status: 'active',
    operatingHours: {
      open: '00:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Torre E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Automatically generate block stats from the units, parkings, amenities arrays
export const mockBlockStats: { [blockId: string]: {
  totalApartmentUnits: number;
  totalParkings: number;
  totalAmenities: number;
  totalResidents: number;
} } = blocks.reduce((acc: { [blockId: string]: any }, block) => {
  const blockId = block.id;
  const blockUnits = units.filter(u => u.block?.id === blockId);
  const blockParkings = parkings.filter(p => p.block?.id === blockId);
  const blockAmenities = amenities.filter(a => a.block?.id === blockId);
  
  // Calculate residents based on occupied units (about 80% occupancy)
  const occupiedUnits = blockUnits.filter(unit => unit.status === 'occupied');
  const totalResidents = occupiedUnits.length;
  
  acc[blockId] = {
    totalApartmentUnits: blockUnits.length,
    totalParkings: blockParkings.length,
    totalAmenities: blockAmenities.length,
    totalResidents: totalResidents
  };
  return acc;
}, {});
